import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader.js';
import { useTenantContext } from '../api/tenant-context.js';
import {
  createUser,
  deleteUser,
  listUsers,
  resetUserPassword,
  updateUser,
  type TenantUser,
  type UserRole,
} from '../api/users.js';
import { updateTenant, type TenantPatch } from '../api/tenants.js';
import {
  createApiKey,
  listApiKeys,
  revokeApiKey,
  type ApiKey,
  type ApiKeyScope,
  type CreateApiKeyResult,
} from '../api/api-keys.js';
import { useAuth } from '../auth/useAuth.js';

const ALL_ROLES: UserRole[] = ['admin', 'editor', 'agent', 'viewer'];
const LOCALES = ['es-CL', 'es', 'en', 'en-US', 'pt-BR'];

/**
 * Configuración del workspace + usuarios + plan + API keys.
 * Workspace y usuarios son reales (PATCH tenant + CRUD users). Plan y
 * API keys siguen como placeholder TODO backend.
 */
export function Configuracion(): JSX.Element {
  const { tenant } = useTenantContext();
  const tenantId = tenant?.id ?? '';
  const qc = useQueryClient();
  const refreshTenant = (): void => {
    void qc.invalidateQueries({ queryKey: ['tenants', 'customer-context'] });
  };
  const { claims } = useAuth();

  const usersQ = useQuery({
    queryKey: ['users', tenantId],
    queryFn: () => listUsers(tenantId),
    enabled: !!tenantId,
  });

  return (
    <>
      <PageHeader
        title="Configuración"
        subtitle="Workspace, usuarios, plan y credenciales API del tenant."
      />
      <div className="grid gap-5">
        <WorkspacePanel tenant={tenant} onSaved={refreshTenant} />

        <UsersPanel
          tenantId={tenantId}
          callerSub={claims?.sub ?? null}
          users={usersQ.data ?? []}
          loading={usersQ.isLoading}
          onChanged={() => qc.invalidateQueries({ queryKey: ['users', tenantId] })}
        />

        <section className="panel">
          <div className="text-[14px] font-semibold mb-3">Plan</div>
          <div className="flex items-center gap-3 mb-3">
            <span className="badge badge-ok">Growth</span>
            <span className="text-ink-3 text-[13px]">
              Hasta 50k mensajes/mes · 5 canales
            </span>
          </div>
          <div className="text-[12.5px] text-ink-4">
            Feature flags activos:{' '}
            {Object.entries(tenant?.featureFlags ?? {})
              .filter(([, v]) => v)
              .map(([k]) => k)
              .join(', ') || '—'}
          </div>
          <div className="mt-3 text-[12px] text-ink-4 italic">
            Billing automatizado (Stripe / AWS Marketplace): próximamente.
          </div>
        </section>

        <ApiKeysPanel tenantId={tenantId} />
      </div>
    </>
  );
}

function WorkspacePanel({
  tenant,
  onSaved,
}: {
  tenant: ReturnType<typeof useTenantContext>['tenant'];
  onSaved: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(tenant?.name ?? '');
  const [locale, setLocale] = useState(tenant?.defaultLocale ?? 'es-CL');
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setName(tenant?.name ?? '');
    setLocale(tenant?.defaultLocale ?? 'es-CL');
  }, [tenant]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!tenant) throw new Error('tenant not loaded');
      const patch: TenantPatch = {};
      if (name.trim() !== tenant.name) patch.name = name.trim();
      if (locale !== (tenant.defaultLocale ?? '')) patch.defaultLocale = locale;
      if (Object.keys(patch).length === 0) return tenant;
      return updateTenant(tenant.id, patch, tenant.version ?? 1);
    },
    onSuccess: () => {
      onSaved();
      setEditing(false);
      setErr(null);
    },
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'error'),
  });

  return (
    <section className="panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[14px] font-semibold">Workspace</div>
        {!editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-[12px] text-brand hover:text-brand-hover"
          >
            Editar
          </button>
        )}
      </div>

      {!editing ? (
        <>
          <DefRow label="Nombre" value={tenant?.name ?? '—'} />
          <DefRow label="Slug" value={tenant?.slug ?? '—'} mono />
          <DefRow label="Tier" value={tenant?.tier ?? '—'} />
          <DefRow label="Estado" value={tenant?.status ?? '—'} />
          <DefRow label="Locale por defecto" value={tenant?.defaultLocale ?? 'es-CL'} />
        </>
      ) : (
        <div className="space-y-3">
          <FormField label="Nombre">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              className="input"
            />
          </FormField>
          <FormField label="Slug" helper="No editable después de creado.">
            <input type="text" value={tenant?.slug ?? ''} readOnly className="input mono opacity-60" />
          </FormField>
          <FormField label="Locale por defecto">
            <select value={locale} onChange={(e) => setLocale(e.target.value)} className="input">
              {LOCALES.map((l) => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </FormField>
          {err && <p className="text-[12px] text-err">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setName(tenant?.name ?? '');
                setLocale(tenant?.defaultLocale ?? 'es-CL');
                setErr(null);
              }}
              className="btn-ghost"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="btn-primary"
            >
              {mutation.isPending ? '…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function UsersPanel({
  tenantId,
  callerSub,
  users,
  loading,
  onChanged,
}: {
  tenantId: string;
  callerSub: string | null;
  users: TenantUser[];
  loading: boolean;
  onChanged: () => void;
}): JSX.Element {
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<TenantUser | null>(null);

  return (
    <section className="panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[14px] font-semibold">Usuarios</div>
          <div className="text-[12px] text-ink-3">
            {users.length} {users.length === 1 ? 'usuario' : 'usuarios'} con acceso al tenant
          </div>
        </div>
        <button type="button" onClick={() => setCreating(true)} className="btn-primary">
          + Invitar usuario
        </button>
      </div>

      {creating && (
        <UserForm
          tenantId={tenantId}
          mode="create"
          onSuccess={() => {
            setCreating(false);
            onChanged();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {loading && <div className="text-ink-3 text-sm">Cargando…</div>}
      {!loading && users.length === 0 && (
        <div className="text-ink-3 text-sm">Sin usuarios.</div>
      )}

      {!loading && users.length > 0 && (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11.5px] text-ink-4">
              <th className="py-2 font-medium">Email</th>
              <th className="py-2 font-medium">Nombre</th>
              <th className="py-2 font-medium">Roles</th>
              <th className="py-2 font-medium">Estado</th>
              <th className="py-2 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <UserRowOrEdit
                key={u.username}
                u={u}
                tenantId={tenantId}
                callerSub={callerSub}
                editing={editing === u.username}
                onEdit={() => setEditing(u.username)}
                onCancelEdit={() => setEditing(null)}
                onSaved={() => {
                  setEditing(null);
                  onChanged();
                }}
                onAskDelete={() => setConfirmDelete(u)}
                onChanged={onChanged}
              />
            ))}
          </tbody>
        </table>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="¿Eliminar usuario?"
          desc={`Se eliminará la cuenta ${confirmDelete.email} de Cognito. Acción irreversible.`}
          confirmLabel="Eliminar"
          onConfirm={async () => {
            await deleteUser(tenantId, confirmDelete.username);
            setConfirmDelete(null);
            onChanged();
          }}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </section>
  );
}

function UserRowOrEdit({
  u,
  tenantId,
  callerSub,
  editing,
  onEdit,
  onCancelEdit,
  onSaved,
  onAskDelete,
  onChanged,
}: {
  u: TenantUser;
  tenantId: string;
  callerSub: string | null;
  editing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaved: () => void;
  onAskDelete: () => void;
  onChanged: () => void;
}): JSX.Element {
  const isSelf = callerSub === u.sub;

  if (editing) {
    return (
      <tr className="border-t border-border">
        <td colSpan={5} className="py-3">
          <UserForm
            tenantId={tenantId}
            mode="edit"
            initial={u}
            onSuccess={onSaved}
            onCancel={onCancelEdit}
          />
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-t border-border">
      <td className="py-3 text-ink-1 mono">{u.email}</td>
      <td className="py-3 text-ink-2">{u.displayName ?? '—'}</td>
      <td className="py-3">
        <div className="flex flex-wrap gap-1">
          {u.roles.map((r) => (
            <span
              key={r}
              className={`badge ${r === 'admin' ? 'badge-brand' : 'badge-neutral'}`}
            >
              {r}
            </span>
          ))}
        </div>
      </td>
      <td className="py-3 text-ink-3 text-[12px] mono">
        {u.status ?? (u.enabled === false ? 'disabled' : 'active')}
      </td>
      <td className="py-3 text-right">
        <div className="inline-flex gap-1">
          <button
            type="button"
            onClick={async () => {
              await resetUserPassword(tenantId, u.username);
              onChanged();
            }}
            className="btn-mini"
            title="Forzar cambio de password"
          >
            Reset
          </button>
          <button type="button" onClick={onEdit} className="btn-mini">
            Editar
          </button>
          {!isSelf && (
            <button
              type="button"
              onClick={onAskDelete}
              className="btn-mini btn-mini-danger"
            >
              Eliminar
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function UserForm({
  tenantId,
  mode,
  initial,
  onSuccess,
  onCancel,
}: {
  tenantId: string;
  mode: 'create' | 'edit';
  initial?: TenantUser;
  onSuccess: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [email, setEmail] = useState(initial?.email ?? '');
  const [displayName, setDisplayName] = useState(initial?.displayName ?? '');
  const [roles, setRoles] = useState<UserRole[]>(initial?.roles ?? ['editor']);
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      if (mode === 'create') {
        if (!email.trim()) throw new Error('Email es obligatorio');
        if (roles.length === 0) throw new Error('Asigná al menos un rol');
        return createUser(tenantId, {
          email: email.trim(),
          ...(displayName.trim() ? { displayName: displayName.trim() } : {}),
          roles,
        });
      }
      if (initial) {
        return updateUser(tenantId, initial.username, {
          displayName: displayName.trim() || undefined,
          roles,
        });
      }
      throw new Error('invalid form state');
    },
    onSuccess: () => onSuccess(),
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'error'),
  });

  return (
    <div className="rounded border border-border bg-bg-2 p-4 space-y-3">
      <div className="text-[13px] font-semibold">
        {mode === 'create' ? 'Invitar nuevo usuario' : `Editar ${initial?.email}`}
      </div>
      <FormField label="Email">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={mode === 'edit'}
          className="input"
        />
      </FormField>
      <FormField label="Nombre (opcional)">
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={120}
          className="input"
        />
      </FormField>
      <FormField label="Roles">
        <div className="flex flex-wrap gap-2">
          {ALL_ROLES.map((r) => (
            <label
              key={r}
              className={`flex items-center gap-2 rounded border px-3 py-1 text-[12px] cursor-pointer ${
                roles.includes(r)
                  ? 'border-brand bg-brand-soft text-ink-1'
                  : 'border-border text-ink-3 hover:border-border-2'
              }`}
            >
              <input
                type="checkbox"
                checked={roles.includes(r)}
                onChange={() => setRoles((p) => (p.includes(r) ? p.filter((x) => x !== r) : [...p, r]))}
              />
              {r}
            </label>
          ))}
        </div>
      </FormField>
      {err && <p className="text-[12px] text-err">{err}</p>}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="btn-ghost">
          Cancelar
        </button>
        <button type="button" onClick={() => mut.mutate()} disabled={mut.isPending} className="btn-primary">
          {mut.isPending ? '…' : mode === 'create' ? 'Invitar' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

function ConfirmModal({
  title,
  desc,
  confirmLabel,
  onConfirm,
  onCancel,
}: {
  title: string;
  desc: string;
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}): JSX.Element {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-md rounded-lg border border-border-2 bg-surface p-5">
        <div className="text-[15px] font-semibold mb-2">{title}</div>
        <p className="text-[13px] text-ink-3 mb-4">{desc}</p>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCancel} className="btn-ghost">
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            className="btn-primary"
            style={{ background: 'var(--err)' }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormField({
  label,
  helper,
  children,
}: {
  label: string;
  helper?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="block mb-1 text-[12px] text-ink-3">{label}</span>
      {children}
      {helper && <span className="block mt-1 text-[10.5px] text-ink-4">{helper}</span>}
    </label>
  );
}

function DefRow({ label, value, mono }: { label: string; value: string; mono?: boolean }): JSX.Element {
  return (
    <div className="flex gap-4 py-2 border-b border-border last:border-b-0">
      <div className="text-[12px] text-ink-4 w-[180px] shrink-0">{label}</div>
      <div className={`text-[13px] ${mono ? 'mono' : ''} text-ink-2`}>{value}</div>
    </div>
  );
}

// ── API Keys panel (F4.4) ─────────────────────────────────────────────────

const SCOPE_OPTIONS: ApiKeyScope[] = [
  'admin',
  'read:all',
  'write:all',
  'channels:read',
  'channels:write',
  'campaigns:read',
  'campaigns:write',
  'flows:read',
  'flows:write',
];

function ApiKeysPanel({ tenantId }: { tenantId: string }): JSX.Element {
  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState<ApiKey | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResult | null>(null);

  const q = useQuery({
    queryKey: ['api-keys', tenantId, { includeRevoked: showRevoked }],
    queryFn: () => listApiKeys(tenantId, { includeRevoked: showRevoked }),
    enabled: !!tenantId,
  });
  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['api-keys', tenantId] });
  };

  const items = q.data ?? [];

  return (
    <section className="panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[14px] font-semibold">API keys</div>
          <div className="text-[12px] text-ink-3">
            Credenciales programáticas para integraciones server-to-server.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-[12px] text-ink-3">
            <input
              type="checkbox"
              checked={showRevoked}
              onChange={(e) => setShowRevoked(e.target.checked)}
            />
            Mostrar revocadas
          </label>
          <button type="button" onClick={() => setCreating(true)} className="btn-primary">
            + Crear API key
          </button>
        </div>
      </div>

      {creating && (
        <ApiKeyForm
          tenantId={tenantId}
          onSuccess={(r) => {
            setCreating(false);
            setCreatedKey(r);
            refresh();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      {createdKey && (
        <CreatedKeyModal
          result={createdKey}
          onClose={() => setCreatedKey(null)}
        />
      )}

      {q.isLoading && <div className="text-ink-3 text-sm">Cargando…</div>}
      {!q.isLoading && items.length === 0 && !creating && (
        <div className="text-ink-3 text-sm">
          No tenés API keys. Creá una para integrar ZEngine con sistemas externos.
        </div>
      )}

      {items.length > 0 && (
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11.5px] text-ink-4">
              <th className="py-2 font-medium">Nombre</th>
              <th className="py-2 font-medium">Prefix</th>
              <th className="py-2 font-medium">Scopes</th>
              <th className="py-2 font-medium">Estado</th>
              <th className="py-2 font-medium">Último uso</th>
              <th className="py-2 font-medium">Expira</th>
              <th className="py-2 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map((k) => (
              <tr key={k.id} className="border-t border-border">
                <td className="py-2.5 text-ink-1">
                  {k.name}
                  {k.description && (
                    <div className="text-[11px] text-ink-4">{k.description}</div>
                  )}
                </td>
                <td className="py-2.5 mono text-[11.5px] text-ink-2">{k.prefix}</td>
                <td className="py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {k.scopes.map((s) => (
                      <span
                        key={s}
                        className={`badge ${s === 'admin' ? 'badge-brand' : 'badge-neutral'}`}
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="py-2.5">
                  <span className={`badge ${k.status === 'active' ? 'badge-ok' : 'badge-neutral'}`}>
                    {k.status}
                  </span>
                </td>
                <td className="py-2.5 text-[11.5px] text-ink-3 mono">
                  {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}
                </td>
                <td className="py-2.5 text-[11.5px] text-ink-3 mono">
                  {k.expiresAt ? new Date(k.expiresAt).toLocaleDateString() : '∞'}
                </td>
                <td className="py-2.5 text-right">
                  {k.status === 'active' && (
                    <button
                      type="button"
                      onClick={() => setConfirmRevoke(k)}
                      className="btn-mini btn-mini-danger"
                    >
                      Revocar
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {confirmRevoke && (
        <ConfirmModal
          title="¿Revocar API key?"
          desc={`"${confirmRevoke.name}" dejará de aceptar requests. Acción irreversible.`}
          confirmLabel="Revocar"
          onConfirm={async () => {
            await revokeApiKey(tenantId, confirmRevoke.id);
            setConfirmRevoke(null);
            refresh();
          }}
          onCancel={() => setConfirmRevoke(null)}
        />
      )}
    </section>
  );
}

function ApiKeyForm({
  tenantId,
  onSuccess,
  onCancel,
}: {
  tenantId: string;
  onSuccess: (r: CreateApiKeyResult) => void;
  onCancel: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scopes, setScopes] = useState<ApiKeyScope[]>(['admin']);
  const [expiresAt, setExpiresAt] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      if (!name.trim()) throw new Error('Nombre obligatorio');
      if (scopes.length === 0) throw new Error('Seleccioná al menos un scope');
      return createApiKey(tenantId, {
        name: name.trim(),
        scopes,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(expiresAt ? { expiresAt: new Date(expiresAt).toISOString() } : {}),
      });
    },
    onSuccess: (r) => onSuccess(r),
    onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'error'),
  });

  return (
    <div className="rounded border border-border bg-bg-2 p-4 space-y-3 mb-4">
      <div className="text-[13px] font-semibold">Crear nueva API key</div>
      <label className="block">
        <span className="block mb-1 text-[12px] text-ink-3">Nombre</span>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={120}
          placeholder="ej. Production Backend"
          className="input"
        />
      </label>
      <label className="block">
        <span className="block mb-1 text-[12px] text-ink-3">Descripción (opcional)</span>
        <textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          className="input"
        />
      </label>
      <div>
        <span className="block mb-1 text-[12px] text-ink-3">Scopes</span>
        <div className="flex flex-wrap gap-2">
          {SCOPE_OPTIONS.map((s) => (
            <label
              key={s}
              className={`flex items-center gap-2 rounded border px-3 py-1 text-[12px] cursor-pointer ${
                scopes.includes(s)
                  ? 'border-brand bg-brand-soft text-ink-1'
                  : 'border-border text-ink-3 hover:border-border-2'
              }`}
            >
              <input
                type="checkbox"
                checked={scopes.includes(s)}
                onChange={() =>
                  setScopes((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]))
                }
              />
              <span className="mono">{s}</span>
            </label>
          ))}
        </div>
      </div>
      <label className="block">
        <span className="block mb-1 text-[12px] text-ink-3">Expira el (opcional)</span>
        <input
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          className="input"
        />
        <span className="block mt-1 text-[10.5px] text-ink-4">
          Vacío = nunca expira.
        </span>
      </label>
      {err && <p className="text-[12px] text-err">{err}</p>}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
        <button type="button" onClick={() => mut.mutate()} disabled={mut.isPending} className="btn-primary">
          {mut.isPending ? '…' : 'Crear key'}
        </button>
      </div>
    </div>
  );
}

function CreatedKeyModal({
  result,
  onClose,
}: {
  result: CreateApiKeyResult;
  onClose: () => void;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-xl rounded-lg border border-border-2 bg-surface p-5 space-y-3">
        <div className="text-[15px] font-semibold">API key creada</div>
        <p className="text-[13px] text-ink-3">
          Copiá el secret <strong className="text-warn">ahora</strong>. No volverá a mostrarse.
          Guardalo en tu vault (AWS Secrets Manager, 1Password, etc.) y borrá esta vista cuando termines.
        </p>
        <div className="rounded border border-border bg-bg-2 p-3">
          <div className="text-[11px] text-ink-4 mb-1">Secret en claro</div>
          <code className="block break-all font-mono text-[12.5px] text-ink-1">
            {result.plainSecret}
          </code>
          <button
            type="button"
            onClick={async () => {
              await navigator.clipboard.writeText(result.plainSecret);
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
            className="btn-mini mt-2"
          >
            {copied ? '✓ Copiado' : 'Copiar al portapapeles'}
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2 text-[12px]">
          <div>
            <div className="text-ink-4">Nombre</div>
            <div className="text-ink-1">{result.name}</div>
          </div>
          <div>
            <div className="text-ink-4">Prefix</div>
            <div className="mono text-ink-1">{result.prefix}</div>
          </div>
        </div>
        <p className="text-[12px] text-ink-4">
          Uso: <code className="mono">Authorization: Bearer {result.plainSecret.slice(0, 20)}…</code>
        </p>
        <div className="flex justify-end pt-2">
          <button type="button" onClick={onClose} className="btn-primary">
            Cerrar (y olvidar el secret)
          </button>
        </div>
      </div>
    </div>
  );
}
